import './overlay.css';
import { PASSAGE, fragmentAt } from '../config/text.js';
import {
  conversions,
  cycleLang,
  htmlLang,
  invitation,
  langGlyph,
  lineOf,
  onLangChange,
  telescopeCaption,
  ui,
} from '../config/i18n.js';
import { Share } from './Share.js';

/**
 * Everything the city says in words.
 *
 * One line of Calvino at a time, pinned to where you are on the stair; a
 * filament down the right edge instead of a scrollbar; a reticle that fills
 * while you hold a gaze; four glyphs and nothing else. There is deliberately
 * no panel anywhere — if a thing can be said by the city, it is not said here.
 *
 * The language lives in `config/i18n.js` and nothing below `ui/` consults it,
 * so changing it costs exactly this: re-label the glyphs, re-say the one line
 * that happens to be on screen, and carry on from the same step of the stair.
 */
export class Overlay {
  /** @param {HTMLElement} root @param {import('../core/App.js').Context} ctx */
  constructor(root, ctx) {
    this.root = root;
    this.ctx = ctx;
    this.current = null;
    this.invitationShown = false;
    this._lastWhisper = 0;

    root.innerHTML = `
      <div class="veil" data-veil>
        <div class="veil__title" data-veil-title></div>
        <div class="veil__sub" data-veil-sub></div>
      </div>

      <div class="filament">
        <div class="filament__trail" data-trail></div>
        <div class="filament__bead" data-bead></div>
      </div>

      <div class="reticle" data-reticle>
        <svg viewBox="0 0 54 54" aria-hidden="true">
          <circle class="reticle__ring" cx="27" cy="27" r="13"></circle>
          <circle class="reticle__arc" data-arc cx="27" cy="27" r="13"
                  stroke-dasharray="81.68" stroke-dashoffset="81.68"></circle>
        </svg>
      </div>

      <div class="passage" data-passage></div>
      <div class="invitation" data-invitation></div>
      <div class="whisper" data-whisper></div>

      <div class="glyphs">
        <button class="glyph is-off" data-sound type="button">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 9v6h4l5 4V5L8 9H4z"></path>
            <path d="M16.5 8.5a5 5 0 0 1 0 7" data-wave1></path>
            <path d="M19.5 6a9 9 0 0 1 0 12" data-wave2></path>
          </svg>
        </button>
        <button class="glyph" data-share type="button">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="4" y="4" width="6.5" height="6.5" rx="1"></rect>
            <rect x="13.5" y="4" width="6.5" height="6.5" rx="1"></rect>
            <rect x="4" y="13.5" width="6.5" height="6.5" rx="1"></rect>
            <path d="M13.5 13.5h3v3h-3zM20 13.5v3M16.5 20H20M13.5 20h0.01"></path>
          </svg>
        </button>
        <button class="glyph glyph--word" data-lang type="button"></button>
        <button class="glyph" data-inspect type="button">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="6"></circle>
            <path d="M15.5 15.5 20 20"></path>
          </svg>
        </button>
      </div>

      <div class="inspector" data-inspector></div>
    `;

    this.el = {
      veil: root.querySelector('[data-veil]'),
      veilTitle: root.querySelector('[data-veil-title]'),
      veilSub: root.querySelector('[data-veil-sub]'),
      passage: root.querySelector('[data-passage]'),
      invitation: root.querySelector('[data-invitation]'),
      whisper: root.querySelector('[data-whisper]'),
      bead: root.querySelector('[data-bead]'),
      trail: root.querySelector('[data-trail]'),
      reticle: root.querySelector('[data-reticle]'),
      arc: root.querySelector('[data-arc]'),
      sound: root.querySelector('[data-sound]'),
      share: root.querySelector('[data-share]'),
      lang: root.querySelector('[data-lang]'),
      inspect: root.querySelector('[data-inspect]'),
      inspector: root.querySelector('[data-inspector]'),
    };

    this.share = new Share(root);

    this.arcLength = 2 * Math.PI * 13;
    this.el.arc.setAttribute('stroke-dasharray', String(this.arcLength));

    this.el.sound.addEventListener('click', () => this.ctx.bus.emit('ui:sound-toggle', {}));
    this.el.inspect.addEventListener('click', () => this.ctx.bus.emit('ui:inspect-toggle', {}));
    this.el.share.addEventListener('click', () => this.share.toggle());
    this.el.lang.addEventListener('click', () => cycleLang());

    this._onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'q' || e.key === 'Q') this.share.toggle();
      else if (e.key === 'l' || e.key === 'L') cycleLang();
      else if (e.key === 'Escape') this.share.close();
    };
    globalThis.addEventListener('keydown', this._onKey);
    this._offLang = onLangChange(() => this.retranslate());

    ctx.bus.on('remember', (e) => this.whisper(e.kind));
    ctx.bus.on('telescope:enter', () => this.caption(telescopeCaption()));
    ctx.bus.on('audio:ready', () => this.el.sound.classList.remove('is-off'));

    this.retranslate();
    this.setFragment(PASSAGE[0], true);
  }

  /** Put every word on screen back into the language now being read. */
  retranslate() {
    const html = document.documentElement;
    html.lang = htmlLang();
    // Chinese does not take the italic the passage is set in, and sets its
    // own line height; the stylesheet keys both off this.
    html.dataset.lang = htmlLang().slice(0, 2);

    document.title = ui('docTitle');
    this.el.veilTitle.textContent = ui('title');
    this.el.veilSub.textContent = ui('subtitle');
    this.el.invitation.textContent = invitation();
    this.el.sound.setAttribute('aria-label', ui('sound'));
    this.el.inspect.setAttribute('aria-label', ui('inspect'));
    this.el.share.setAttribute('aria-label', ui('share'));
    this.el.lang.setAttribute('aria-label', ui('language'));
    this.el.lang.textContent = langGlyph();

    // The line on screen is replaced rather than crossfaded: it is the same
    // moment of the passage, only said differently.
    const held = this.current;
    if (held) {
      this.el.passage.replaceChildren();
      this.current = null;
      this.setFragment(held, true);
    }

    this.share.retranslate();
  }

  lift() {
    this.el.veil.classList.add('is-gone');
    setTimeout(() => {
      if (!this.invitationShown) {
        this.el.invitation.classList.add('is-in');
        this.invitationShown = true;
        setTimeout(() => this.el.invitation.classList.remove('is-in'), 11000);
      }
    }, 1400);
  }

  setFragment(fragment, immediate = false) {
    if (this.current === fragment) return;
    this.current = fragment;

    const old = this.el.passage.querySelector('.passage__line.is-in');
    if (old) {
      old.classList.remove('is-in');
      old.classList.add('is-out');
      setTimeout(() => old.remove(), 1700);
    }

    const line = document.createElement('span');
    line.className = 'passage__line';
    line.textContent = lineOf(fragment);
    this.el.passage.appendChild(line);
    this.el.passage.classList.toggle('is-final', !!fragment.final);

    // Force a reflow so the transition runs from the start state.
    void line.offsetWidth;
    if (immediate) line.classList.add('is-in');
    else requestAnimationFrame(() => line.classList.add('is-in'));
  }

  whisper(kind) {
    const now = performance.now();
    if (now - this._lastWhisper < 2400) return;
    const pool = conversions(kind);
    if (!pool) return;
    this._lastWhisper = now;
    const el = this.el.whisper;
    el.textContent = pool[Math.floor(Math.random() * pool.length)];
    el.classList.remove('is-in');
    void el.offsetWidth;
    el.classList.add('is-in');
  }

  caption(text) {
    const el = this.el.whisper;
    el.textContent = text;
    el.classList.remove('is-in');
    void el.offsetWidth;
    el.classList.add('is-in');
  }

  setSound(on) {
    this.el.sound.classList.toggle('is-off', !on);
  }

  /** @param {import('../core/App.js').FrameState} state */
  update(state) {
    this.setFragment(fragmentAt(state.t));

    const pct = state.t * 100;
    this.el.bead.style.top = `${pct}%`;
    this.el.trail.style.height = `${pct}%`;

    const gaze = this.ctx.gaze;
    const showing = !!gaze.current && state.iris < 0.25;
    this.el.reticle.classList.toggle('is-on', showing);
    const p = gaze.current ? gaze.progress : 0;
    this.el.arc.setAttribute('stroke-dashoffset', String(this.arcLength * (1 - p)));
  }

  setInspector(on, text) {
    this.el.inspector.classList.toggle('is-on', on);
    if (on) this.el.inspector.innerHTML = text;
  }

  dispose() {
    globalThis.removeEventListener('keydown', this._onKey);
    this._offLang();
    this.share.dispose();
  }
}
